import React, { useEffect, useState } from 'react'
import { useOne, useUpdate } from '@refinedev/core'
import { App, Button, Input, Select, Switch, Tooltip } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, type Stat } from '../../theme/listkit'
import { HandHeart, Target, Settings } from '../../components/icons'

// The row's PK `id` is a boolean (`true`), which conflicts with Refine's
// BaseRecord (`id?: string | number`). Drop it from the hook's generic — the page
// never reads id, only the settings columns — and address the singleton by the
// string 'true' below (the Supabase provider issues `id=eq.true`).
type GivingSettings = Omit<Tables<'giving_settings'>, 'id'>

/**
 * Singleton contract: giving_settings holds exactly one row whose boolean primary
 * key id is `true` (mirrors app_config). Read/write it via id = true. Refine's
 * BaseKey is string | number, so we pass the string 'true' — the Supabase data
 * provider issues `id=eq.true`, which PostgREST coerces to the boolean column.
 */
const GIVING_SETTINGS_ID = 'true'
const GIVING_SETTINGS_META = { idColumnName: 'id' } as const

type Provider = 'stripe' | 'cheddarup'

const PROVIDER_OPTIONS: { value: Provider; label: string }[] = [
  { value: 'stripe', label: 'Stripe' },
  { value: 'cheddarup', label: 'Cheddar Up' },
]

const providerLabel = (p: string): string =>
  PROVIDER_OPTIONS.find((o) => o.value === p)?.label ?? p

/**
 * Giving Settings — the singleton control for how the app collects gifts. Choose
 * the active payment provider (Stripe's in-app PaymentSheet, or Cheddar Up's
 * hosted collection page), set the Cheddar Up collection URL members are sent to,
 * and decide whether processing fees are passed on to the donor. The mobile app
 * reads this row (RLS: public SELECT) and subscribes to it via Realtime, so a
 * change here takes effect for members within seconds — no deploy required.
 */
export function GivingSettingsPage() {
  const { message } = App.useApp()

  const { query } = useOne<GivingSettings>({
    resource: 'giving_settings',
    id: GIVING_SETTINGS_ID,
    meta: GIVING_SETTINGS_META,
  })
  const { mutate: update } = useUpdate()

  const row = query.data?.data
  const isLoading = query.isLoading

  // Local draft synced from the row whenever it (re)loads. Save writes the draft
  // back to the singleton; the provider's own SELECT-after-UPDATE refreshes it.
  const [provider, setProvider] = useState<Provider>('stripe')
  const [cheddarupUrl, setCheddarupUrl] = useState('')
  const [passFees, setPassFees] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!row) return
    setProvider((row.provider as Provider) ?? 'stripe')
    setCheddarupUrl(row.cheddarup_collection_url ?? '')
    setPassFees(row.pass_fees ?? true)
  }, [row])

  const dirty =
    row != null &&
    (provider !== ((row.provider as Provider) ?? 'stripe') ||
      cheddarupUrl.trim() !== (row.cheddarup_collection_url ?? '').trim() ||
      passFees !== (row.pass_fees ?? true))

  function save() {
    setSaving(true)
    update(
      {
        resource: 'giving_settings',
        id: GIVING_SETTINGS_ID,
        values: {
          provider,
          cheddarup_collection_url: cheddarupUrl.trim() || null,
          pass_fees: passFees,
        },
        meta: GIVING_SETTINGS_META,
      },
      {
        onSuccess: () => {
          setSaving(false)
          message.success('Giving settings saved.')
        },
        onError: (e) => {
          setSaving(false)
          const msg = e instanceof Error ? e.message : String(e)
          message.error(`Failed to save giving settings: ${msg}`)
        },
      },
    )
  }

  const isCheddarup = provider === 'cheddarup'

  const stats: Stat[] = [
    {
      label: 'Active provider',
      value: isLoading ? '…' : providerLabel(provider),
      icon: HandHeart,
      tone: isCheddarup ? 'jaguar' : 'gold',
    },
    {
      label: 'Processing fees',
      value: isLoading ? '…' : passFees ? 'Passed to donor' : 'Absorbed',
      icon: Target,
      tone: passFees ? 'green' : 'neutral',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Giving Settings',
        description:
          'Choose how the app collects gifts. Stripe charges in-app with Apple/Google Pay; Cheddar Up sends members to a hosted collection page and posts each gift back via webhook. Set the Cheddar Up link and whether processing fees are added to the donor’s total. Changes take effect within seconds — no deploy required.',
      }}
      stats={stats}
      statColumns={2}
    >
      <SectionCard
        title="Payment provider"
        sub="Where members complete a gift"
        style={{ maxWidth: 720 }}
      >
        {isLoading ? (
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 500,
              fontSize: 13,
              color: brand.sub,
              padding: '24px 0',
            }}
          >
            Loading giving settings…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {/* Provider */}
            <div>
              <FieldLabel icon={<Settings size={15} color={brand.jaguar} />}>Provider</FieldLabel>
              <Select<Provider>
                data-testid="giving-provider-select"
                value={provider}
                onChange={(v) => setProvider(v)}
                options={PROVIDER_OPTIONS}
                style={{ width: 260 }}
              />
              <FieldHint>
                Stripe uses the in-app PaymentSheet. Cheddar Up opens a hosted collection page and
                confirms each gift through the webhook.
              </FieldHint>
            </div>

            {/* Cheddar Up collection URL */}
            <div>
              <FieldLabel icon={<HandHeart size={15} color={brand.gold} />}>
                Cheddar Up collection URL
              </FieldLabel>
              <Input
                data-testid="giving-cheddarup-url"
                value={cheddarupUrl}
                placeholder="https://muaa.cheddarup.com/c/..."
                onChange={(e) => setCheddarupUrl(e.target.value)}
                style={{ maxWidth: 460, fontFamily: brand.fSans }}
              />
              <FieldHint>
                {isCheddarup
                  ? 'Members are sent here to give. Per-fund links (Funds → Cheddar Up URL) override this collection-wide default.'
                  : 'Only used when Cheddar Up is the active provider. Safe to set ahead of switching.'}
              </FieldHint>
            </div>

            {/* Pass fees */}
            <div
              data-testid="giving-pass-fees-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '16px 14px',
                borderRadius: 10,
                background: 'rgba(196,151,49,.06)',
                border: '1px solid rgba(196,151,49,.22)',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 13,
                    color: brand.ink,
                    marginBottom: 3,
                  }}
                >
                  Pass processing fees to the donor
                </div>
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 500,
                    fontSize: 12,
                    color: brand.sub,
                    lineHeight: 1.5,
                  }}
                >
                  On = the donor’s total is grossed up so the fund receives the full gift. Off = the
                  fee is deducted from the gift.
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <Tooltip title={passFees ? 'Stop adding fees to the donor total' : 'Add fees to the donor total'}>
                  <Switch
                    data-testid="giving-pass-fees"
                    checked={passFees}
                    onChange={(checked) => setPassFees(checked)}
                    style={{ background: passFees ? brand.gold : undefined }}
                  />
                </Tooltip>
              </div>
            </div>

            {/* Save */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button
                data-testid="giving-save"
                type="primary"
                loading={saving}
                disabled={!dirty}
                onClick={save}
              >
                Save settings
              </Button>
              {dirty && (
                <span style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
                  Unsaved changes
                </span>
              )}
              {row?.updated_at && !dirty && (
                <span style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub, opacity: 0.7 }}>
                  Last changed {new Date(row.updated_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}

/** Small brand-styled field label with a leading icon. */
function FieldLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: brand.fSans,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: brand.sub,
        marginBottom: 8,
      }}
    >
      {icon}
      {children}
    </div>
  )
}

/** One-line helper text under a field. */
function FieldHint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: brand.fSans,
        fontWeight: 500,
        fontSize: 12,
        color: brand.sub,
        lineHeight: 1.5,
        marginTop: 7,
        maxWidth: 520,
      }}
    >
      {children}
    </div>
  )
}
